import { Http } from '@mygpt/app/http';

// MCP配置接口定义
export interface McpConfig {
  id: string;
  name: string;
  description: string;
  available_tools: string;
  json_config?: Record<string, any> | string;
  is_enabled: boolean | number; // API返回可能是 boolean 或 number (0/1)
  type?: number; // 1为MCP URL，2为JSON配置
  bot_id?: string | null; // bot_id存在表示是bot级别的MCP，为空表示是公用MCP
  created_at: string;
  updated_at: string;
}

// 创建MCP配置请求接口
export interface CreateMcpConfigRequest {
  name: string;
  description: string;
  available_tools: string;
  json_config: Record<string, any>;
  is_enabled: boolean | number; // 允许 boolean 或 number (0/1)
  type?: number; // 1为MCP URL，2为JSON配置
  bot_id?: string; // bot_id存在表示创建bot级别的MCP，为空表示创建公用MCP
}

// MCP配置列表响应接口
export interface McpConfigListResponse {
  items: McpConfig[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

// 分页参数接口
export interface McpConfigListParams {
  page?: number;
  size?: number;
}

/**
 * 获取MCP配置列表
 * @param params 分页参数
 * @returns MCP配置列表
 */
export const getMcpConfigs = async (params: McpConfigListParams = {}): Promise<McpConfigListResponse> => {
  const { get } = Http.use();
  const { page = 1, size = 10 } = params;

  const response = await get<McpConfigListResponse>(`/admin/mcp-configs`, {
    params: { page, size },
  });

  return response.data;
};

/**
 * 获取单个MCP配置详情
 * @param id MCP配置ID
 * @returns MCP配置详情
 */
export const getMcpConfig = async (id: string): Promise<McpConfig> => {
  const { get } = Http.use();

  const response = await get<McpConfig>(`/admin/mcp-configs/${id}`);

  return response.data;
};

/**
 * 创建MCP配置
 * @param data 创建MCP配置请求数据
 * @returns 创建的MCP配置
 */
export const createMcpConfig = async (data: CreateMcpConfigRequest): Promise<McpConfig> => {
  const { post } = Http.use();

  const response = await post<McpConfig>(`/admin/mcp-configs`, data);

  return response.data;
};

/**
 * 更新MCP配置
 * @param id MCP配置ID
 * @param data 更新数据
 * @returns 更新后的MCP配置
 */
export const updateMcpConfig = async (id: string, data: Partial<CreateMcpConfigRequest>): Promise<McpConfig> => {
  const { put } = Http.use();

  const response = await put<McpConfig>(`/admin/mcp-configs/${id}`, data);

  return response.data;
};

/**
 * 删除MCP配置
 * @param id MCP配置ID
 * @returns 删除结果
 */
export const deleteMcpConfig = async (id: string): Promise<void> => {
  const { delete: deleteHttp } = Http.use();

  await deleteHttp(`/admin/mcp-configs/${id}`);
};

/**
 * 启用/禁用MCP配置
 * @param id MCP配置ID
 * @param enabled 是否启用
 * @returns 更新后的MCP配置
 */
export const toggleMcpConfig = async (id: string, enabled: boolean): Promise<McpConfig> => {
  const { patch } = Http.use();

  const response = await patch<McpConfig>(`/admin/mcp-configs/${id}/toggle`, {
    is_enabled: enabled ? 1 : 0,
  });

  return response.data;
};

// ============= Bot级别的MCP API =============

// Bot MCP服务接口定义（用于/bots/{botId}/mcp-services接口）
export interface BotMcpService {
  id: string;
  name: string;
  description: string;
  available_tools: string;
  is_enabled_for_bot: boolean;
  enabled_at: string | null;
  scope: 'global' | 'bot_level'; // global为公用MCP，bot_level为bot级别的MCP
  bot_id?: string | null; // bot级别MCP的bot_id
  type?: number; // 1为MCP URL，2为JSON配置
  json_config?: Record<string, any> | string;
}

// Bot MCP服务列表响应接口
export interface BotMcpServiceListResponse {
  items: BotMcpService[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

/**
 * 获取Bot可用的MCP服务列表（包含公用MCP和bot自己创建的MCP）
 * @param botId Bot ID
 * @param params 分页参数
 * @returns Bot MCP服务列表
 */
export const getBotMcpServices = async (
  botId: string,
  params: McpConfigListParams = {}
): Promise<BotMcpServiceListResponse> => {
  const { get } = Http.use();
  const { page = 1, size = 10 } = params;

  const response = await get<BotMcpServiceListResponse>(`/bots/${botId}/mcp-services`, {
    params: { page, size },
  });

  return response.data;
};

/**
 * 创建Bot级别的MCP配置
 * @param botId Bot ID
 * @param data 创建MCP配置请求数据
 * @returns 创建的MCP配置
 */
export const createBotMcpConfig = async (botId: string, data: CreateMcpConfigRequest): Promise<McpConfig> => {
  const { post } = Http.use();

  const response = await post<McpConfig>(`/admin/mcp-configs`, {
    ...data,
    bot_id: botId,
  });

  return response.data;
};

/**
 * 启用/禁用Bot的MCP服务
 * @param botId Bot ID
 * @param serviceId MCP服务ID
 * @param enabled 是否启用
 * @returns 启用/禁用结果
 */
export const toggleBotMcpService = async (
  botId: string,
  serviceId: string,
  enabled: boolean
): Promise<{ is_enabled: boolean; enabled_at: string | null }> => {
  const { post } = Http.use();

  const response = await post<{ is_enabled: boolean; enabled_at: string | null }>(
    `/bots/${botId}/mcp-services/${serviceId}/toggle`,
    {
      enabled: enabled,
    }
  );

  return response.data;
};
